import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import Analytics from 'packages/analytics';
import { signIn, openNotificationOptinModalAction } from '../ui/actions';
import { postWishlistItem } from './api';
import { API_ERRORS } from 'tradera/utils/api';
import { MODAL_TYPES } from 'tradera/state/push-notifications/constants';
import { hideWishListNudge } from 'tradera/apps/tradera/state/wish-list-nudge/reducer';
import cookieUtil from 'tradera/utils/cookie';
import {
    RECENTLY_VIEWED_ITEM_IDS,
    APP_DOWNLOAD_MODAL_DISMISSED
} from 'tradera/constants/cookies';
import type { AppDispatch, GetState } from '../configure-store';
import {
    wishlist as setWishlistExists,
    addRecentlyViewed as addRecentlyViewedAction,
    wishlistCountIncrement
} from './reducer';
import type { SOURCE_SCREEN } from 'tradera/services/google-tagmanager/constants';

const RECENTLY_VIEWED_ITEM_IDS_TTL = 30 * 24 * 60 * 60 * 1000;

export const addRecentlyViewed =
    (itemId: number) => async (dispatch: AppDispatch, getState: GetState) => {
        dispatch(addRecentlyViewedAction(itemId));

        const {
            items: { recentItemIds }
        } = getState();

        cookieUtil.createCookie(
            RECENTLY_VIEWED_ITEM_IDS,
            recentItemIds.join(';'),
            new Date(new Date().getTime() + RECENTLY_VIEWED_ITEM_IDS_TTL)
        );
    };

type WishListOptions = {
    value?: boolean;
    shouldNotTrackEventBecauseNeedsToBeSameAsLegacyVip?: boolean;
    showWishlistModals?: boolean;
    sectionGaName?: string;
    gaSourceScreen: SOURCE_SCREEN;
    gaWishlistType?: string;
};

/**
 * Toggle item in wishlist
 */
export const wishlist =
    (
        itemId: number,
        {
            value,
            shouldNotTrackEventBecauseNeedsToBeSameAsLegacyVip = false,
            showWishlistModals = true,
            sectionGaName,
            gaSourceScreen,
            gaWishlistType = 'Item card'
        }: WishListOptions
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const {
            member: { isLoggedIn, memberId },
            items: { wishListItemIds },
            page
        } = getState();
        let existsInWishList: boolean;
        if (value === undefined) {
            existsInWishList = wishListItemIds.includes(itemId);
        } else {
            existsInWishList = !value;
        }

        const redirectUrl = window.location.toString();
        const onSignedOutError = () => dispatch(signIn({ redirectUrl }));
        if (!isLoggedIn) {
            onSignedOutError();
            return;
        }

        dispatch(setWishlistExists({ itemId, exists: !existsInWishList }));

        if (!existsInWishList) {
            GtmService.trackGtmEvent('add-to-wishlist', {
                memberId: memberId,
                itemId: itemId
            });
            dispatch(hideWishListNudge());
        }
        if (!shouldNotTrackEventBecauseNeedsToBeSameAsLegacyVip) {
            Analytics.trackEvent({
                category: 'Wish list',
                action: existsInWishList ? 'remove' : 'add',
                label: page.type
            });
        }

        GtmService.trackGA4(
            existsInWishList ? 'remove_from_wishlist' : 'add_to_wishlist',
            {
                item_id: itemId,
                items: [{ item_id: itemId }],
                source_section: sectionGaName,
                source_screen: gaSourceScreen,
                wishlist_type: gaWishlistType
            }
        );

        const shouldAddToWishlist = !existsInWishList;

        return postWishlistItem(itemId, shouldAddToWishlist)
            .then(async () => {
                if (shouldAddToWishlist && showWishlistModals) {
                    dispatch(wishlistCountIncrement());
                }
                if (showWishlistModals && shouldAddToWishlist) {
                    dispatch(showNotificationOptinModalWhenApplicable());
                }
                return shouldAddToWishlist;
            })
            .catch((error) => {
                // Restore previous wishlist status on error
                dispatch(
                    setWishlistExists({
                        itemId,
                        exists: existsInWishList
                    })
                );
                if (error.message === API_ERRORS.LOGGED_OUT) {
                    onSignedOutError();
                }
            });
    };

const showNotificationOptinModalWhenApplicable =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        const appDownloadModalDismissed =
            getState().memberStorage.data[APP_DOWNLOAD_MODAL_DISMISSED] === '1';
        if (appDownloadModalDismissed) {
            dispatch(
                openNotificationOptinModalAction(
                    MODAL_TYPES.wishlistReminders,
                    'Enable - Wishlist - Reminders (from alert)',
                    'pushModal_wishlist_title',
                    'pushModal_wishlist_description',
                    ['wishlistReminders']
                )
            );
        }
    };
